import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Phone, ShieldCheck, ArrowRight, Check, Loader2 } from "lucide-react";
import { FORM_SERVICES, fireAdsConversion, isGoogleVisitor, leadMeta } from "@/components/QuoteForm";
import { trackConversion } from "@/components/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APPOINTMENT_SERVICE = "Rendez-vous diagnostic";

const COUNTRIES = [
  { code: "BE", dial: "+32", flag: "🇧🇪" },
  { code: "FR", dial: "+33", flag: "🇫🇷" },
  { code: "LU", dial: "+352", flag: "🇱🇺" },
  { code: "NL", dial: "+31", flag: "🇳🇱" },
];

// Returns the E.164 number or null when the input is not a plausible phone number.
const normalizePhone = (raw, dial) => {
  const trimmed = (raw || "").trim();
  if (!trimmed || !/^[+\d][\d\s().\-/]*$/.test(trimmed)) return null;
  let p = trimmed.replace(/[\s().\-/]/g, "");
  if (p.startsWith("00")) p = "+" + p.slice(2);
  if (!p.startsWith("+")) p = dial + (p.startsWith("0") ? p.slice(1) : p);
  return /^\+\d{9,15}$/.test(p) ? p : null;
};

const inputClass =
  "w-full bg-white/[0.06] border border-white/15 focus:border-[#D90000] text-white placeholder:text-white/35 text-[15px] px-4 py-3.5 outline-none transition-colors rounded-md";

const AppointmentCard = ({ className = "" }) => {
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [details, setDetails] = useState({ prenom: "", vehicule: "", service: "" });

  const submitPhone = async (e) => {
    e.preventDefault();
    if (busy) return;
    const e164 = normalizePhone(phone, country.dial);
    if (!e164) {
      setError("Numéro invalide. Ex. 0470 12 34 56 ou +32 470 12 34 56");
      return;
    }
    setError("");
    setBusy(true);
    const stored = country.code === "BE" && phone.trim().startsWith("0") ? phone.trim() : e164;
    try {
      const { data } = await axios.post(`${API}/quotes`, {
        telephone: stored,
        service: APPOINTMENT_SERVICE,
        source: isGoogleVisitor() ? "Google" : "Site",
        message: "",
        ...leadMeta("landing_call"),
      });
      setLeadId(data.id);
      setStep(2);
      // Tracking after the UI has moved on: a tracking failure can never block the client.
      try {
        await fireAdsConversion("", e164);
        trackConversion("generate_lead", { service: APPOINTMENT_SERVICE, value: 1, currency: "EUR" });
        if (typeof window.gtag === "function") window.gtag("event", "ads_conversion_Demande_de_devis_1", {});
      } catch (trackErr) {
        console.error("Tracking error", trackErr);
      }
    } catch (err) {
      setError("Une erreur est survenue. Réessayez ou appelez-nous au 0497 13 02 65.");
    } finally {
      setBusy(false);
    }
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    if (busy) return;
    const payload = Object.fromEntries(Object.entries(details).filter(([, v]) => v.trim()));
    if (!Object.keys(payload).length) {
      setStep(3);
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/quotes/${leadId}`, payload);
    } catch (err) {
      // Lead already saved at step 1; never block the client here.
    } finally {
      setBusy(false);
      setStep(3);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      data-testid="appointment-card"
      className={`relative bg-black/55 backdrop-blur-xl border border-white/15 rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(217,0,0,0.08)] p-6 sm:p-8 ${className}`}
    >
      <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D90000] to-transparent" />

      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[#D90000]/15 border border-[#D90000]/40 flex items-center justify-center shrink-0">
          <CalendarDays size={18} className="text-[#D90000]" />
        </div>
        <h2 data-testid="appointment-title" className="font-impact text-3xl sm:text-[34px] leading-none tracking-wide text-white">
          PRENDRE RENDEZ-VOUS
        </h2>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {step === 1 && (
          <motion.form
            key="step1"
            onSubmit={submitPhone}
            noValidate
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35 }}
            data-testid="appointment-step-phone"
          >
            <p data-testid="appointment-subtitle" className="text-white/60 text-sm mb-6">
              Indiquez simplement votre numéro de téléphone
            </p>

            <div className={`flex rounded-md border ${error ? "border-[#FF3B3B]" : "border-white/15"} focus-within:border-[#D90000] bg-white/[0.06] transition-colors`}>
              <select
                data-testid="appointment-country-select"
                value={country.code}
                onChange={(e) => setCountry(COUNTRIES.find((c) => c.code === e.target.value))}
                aria-label="Indicatif pays"
                className="bg-transparent text-white text-sm font-semibold pl-3 pr-2 py-3.5 outline-none border-r border-white/15 cursor-pointer appearance-none"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code} className="bg-black text-white">
                    {c.flag} {c.code} {c.dial}
                  </option>
                ))}
              </select>
              <div className="relative flex-1">
                <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  data-testid="appointment-phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder="Ex. 0470 12 34 56"
                  className="w-full bg-transparent text-white placeholder:text-white/35 text-[15px] pl-10 pr-4 py-3.5 outline-none"
                />
              </div>
            </div>
            {error && (
              <p data-testid="appointment-phone-error" role="alert" className="text-[#FF3B3B] text-xs mt-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="appointment-continue-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-[#D90000] hover:bg-[#FF1A1A] disabled:opacity-60 text-white text-[12px] uppercase tracking-[0.25em] font-bold py-4 rounded-md transition-colors btn-sweep"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {busy ? "Envoi..." : "Continuer"}
              {!busy && <ArrowRight size={16} />}
            </button>

            <p data-testid="appointment-reassurance" className="mt-4 flex items-center gap-2 text-[11px] text-white/45">
              <ShieldCheck size={14} className="text-[#D90000] shrink-0" />
              Aucun spam, juste un rappel de notre équipe
            </p>
          </motion.form>
        )}

        {step === 2 && (
          <motion.form
            key="step2"
            onSubmit={submitDetails}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35 }}
            data-testid="appointment-step-details"
            className="space-y-3"
          >
            <p className="text-white/60 text-sm mb-5">
              <span className="text-white font-semibold">Numéro bien reçu.</span> Quelques précisions (facultatif) pour préparer votre diagnostic :
            </p>
            <input
              data-testid="appointment-firstname-input"
              value={details.prenom}
              onChange={(e) => setDetails({ ...details, prenom: e.target.value })}
              placeholder="Prénom"
              className={inputClass}
            />
            <input
              data-testid="appointment-vehicle-input"
              value={details.vehicule}
              onChange={(e) => setDetails({ ...details, vehicule: e.target.value })}
              placeholder="Véhicule (marque & modèle)"
              className={inputClass}
            />
            <select
              data-testid="appointment-service-select"
              value={details.service}
              onChange={(e) => setDetails({ ...details, service: e.target.value })}
              className={`${inputClass} ${details.service ? "text-white" : "text-white/35"}`}
            >
              <option value="" className="bg-black text-white/60">Service souhaité</option>
              {FORM_SERVICES.map((s) => (
                <option key={s.id} value={s.title} className="bg-black text-white">
                  {s.title}
                </option>
              ))}
              <option value="Je ne sais pas encore" className="bg-black text-white">Je ne sais pas encore</option>
            </select>

            <button
              type="submit"
              disabled={busy}
              data-testid="appointment-send-btn"
              className="!mt-5 w-full inline-flex items-center justify-center gap-3 bg-[#D90000] hover:bg-[#FF1A1A] disabled:opacity-60 text-white text-[12px] uppercase tracking-[0.25em] font-bold py-4 rounded-md transition-colors btn-sweep"
            >
              {busy ? "Envoi..." : "Envoyer"}
              {!busy && <ArrowRight size={16} />}
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              data-testid="appointment-skip-link"
              className="w-full text-center text-[11px] uppercase tracking-[0.2em] text-white/45 hover:text-white transition-colors py-1"
            >
              Passer
            </button>
          </motion.form>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            data-testid="appointment-step-done"
            className="pt-2"
          >
            <div className="w-12 h-12 border-2 border-[#D90000] flex items-center justify-center mb-5">
              <Check size={24} className="text-[#D90000]" strokeWidth={2.5} />
            </div>
            <p data-testid="appointment-success-message" className="text-white/85 text-[15px] leading-relaxed">
              Merci ! Votre demande est bien reçue. VistaWash vous rappelle au plus vite, sous 24h au maximum.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AppointmentCard;
